import { NovuProvider } from '@novu/react'
import { novuConfig } from '@/config/novu.config'
import { useAppSelector } from '@/store/hooks'
import { ReactNode, useMemo } from 'react'
import {
  NotificationCountProviderWithNovu,
  NotificationCountProviderFallback
} from '@/contexts/NotificationCountContext'
import {
  NotificationCacheProvider,
  NotificationCacheProviderFallback
} from '@/contexts/NotificationCacheContext'

interface NovuProviderWrapperProps {
  children: ReactNode
}

export function NovuProviderWrapper({ children }: NovuProviderWrapperProps) {
  // 从 Redux store 获取当前用户 ID 和 session ID
  const userId = useAppSelector((state) => state.user.userId)
  const sessionId = useAppSelector((state) => state.auth.sessionId)

  // Memoize the subscriberId to prevent unnecessary re-initialization
  const subscriberId = useMemo(() => userId?.toString() || '', [userId])

  // 🔧 NovuProviderWrapper 现在只在 Layout 内部使用（已通过 AuthGuard）
  // 此时 userId 应该已经存在，但为了安全仍然保留 fallback
  if (!userId) {
    return (
      <NotificationCountProviderFallback>
        <NotificationCacheProviderFallback>
          {children}
        </NotificationCacheProviderFallback>
      </NotificationCountProviderFallback>
    )
  }

  // 用户已登录，使用完整的 Novu 功能
  // Use sessionId as key to force complete re-mount on each login
  // This clears Novu SDK's internal cache and creates fresh client instance
  return (
    <NovuProvider
      key={sessionId || subscriberId}
      applicationIdentifier={novuConfig.applicationIdentifier}
      subscriberId={subscriberId}
      backendUrl={novuConfig.backendUrl}
      socketUrl={novuConfig.socketUrl}
    >
      <NotificationCountProviderWithNovu>
        <NotificationCacheProvider>
          {children}
        </NotificationCacheProvider>
      </NotificationCountProviderWithNovu>
    </NovuProvider>
  )
}
